import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { onValue, ref, type Database } from 'firebase/database';
import { fetchInbox, type InboxParams } from './api';
import type { ChatConversationListItem } from './types';

interface Options {
  db: Database | null;
  params: InboxParams;
  enabled: boolean;
}

// RTDB pushes are coalesced into a single REST refetch within this window so
// bursts of events (new message + unread bump + status flip) cause one fetch.
const REFETCH_DEBOUNCE_MS = 250;

export function useAdminInbox({ db, params, enabled }: Options) {
  const [items, setItems] = useState<ChatConversationListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const key = JSON.stringify(params);
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const loadInbox = useCallback(() => {
    setLoading(true);
    return fetchInbox(paramsRef.current)
      .then((rows) => setItems(rows))
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void loadInbox();
    // params is captured via the stringified key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key]);

  // RTDB is a change-ping, not a data source. The /admin-inbox tree can carry
  // stale fields (e.g. legacy status from before the backend started writing
  // it). Postgres is authoritative — every push triggers a debounced REST
  // refetch instead of merging RTDB values into local state.
  useEffect(() => {
    if (!db || !enabled) return;
    let timer: number | null = null;
    let firstSnapshot = true;
    const off = onValue(
      ref(db, '/admin-inbox'),
      () => {
        // The initial snapshot replays the whole tree. We've already loaded
        // authoritative data via REST, so ignore it.
        if (firstSnapshot) {
          firstSnapshot = false;
          return;
        }
        if (timer) window.clearTimeout(timer);
        timer = window.setTimeout(() => void loadInbox(), REFETCH_DEBOUNCE_MS);
      },
      (e) => setError(e as Error),
    );
    return () => {
      if (timer) window.clearTimeout(timer);
      off();
    };
  }, [db, enabled, loadInbox]);

  // Defensive local filter. REST already filters by status server-side, but
  // optimistic patches (e.g. close button) can flip statuses ahead of the
  // refetch — this keeps the wrong-tab row from flashing.
  const filtered = useMemo(() => {
    const wanted = params.status ?? 'open';
    if (wanted === 'all') return items;
    return items.filter((i) => (i.status ?? 'open') === wanted);
  }, [items, params.status]);

  const totalUnread = useMemo(
    () => filtered.reduce((acc, i) => acc + (i.unread_for_admin ?? 0), 0),
    [filtered],
  );

  const patchStatus = useCallback((id: string, status: 'open' | 'closed') => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
  }, []);

  return { items: filtered, loading, error, totalUnread, patchStatus, refetch: loadInbox };
}
